// src/pages/client/news/NewsList.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Container from "../../../components/Container";
import axios from "axios";
import { useTranslation } from "react-i18next";

export default function NewsList() {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0); // 0-based
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}`;
  };

  const load = async (pageIndex = 0) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/api/news/list", {
        params: { page: pageIndex, size },
      });

      const resultCode = res.data?.resultCode;
      if (resultCode !== "200" && resultCode !== 200) {
        setError(
          res.data?.resultMessage || "News&Event 목록을 불러오지 못했습니다."
        );
        setItems([]);
        return;
      }

      const data = res.data?.data;

      // ① Spring Data Page 형태 (content, totalPages, totalElements, number ...)
      if (data && Array.isArray(data.content)) {
        setItems(data.content);
        setPage(data.number ?? pageIndex);
        setTotalPages(data.totalPages ?? 1);
        setTotalElements(data.totalElements ?? data.content.length);
      }
      // ② 단순 배열 형태
      else if (Array.isArray(data)) {
        setItems(data);
        setPage(0);
        setTotalPages(1);
        setTotalElements(data.length);
      } else {
        setItems([]);
        setTotalPages(1);
        setTotalElements(0);
      }
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage ||
          "News&Event 목록을 불러오는 중 오류가 발생했습니다."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onPrev = () => {
    setPage((p) => Math.max(0, p - 1));
  };

  const onNext = () => {
    setPage((p) => Math.min(totalPages - 1, p + 1));
  };

  const getPreviewText = (item) => {
    const raw = item.content || "";
    if (!raw) return "";
    const plain =
      typeof raw === "string" ? raw.replace(/<[^>]+>/g, "") : String(raw);
    return plain.length > 120 ? `${plain.slice(0, 120)}...` : plain;
  };

  return (
    <main className="bg-white">
      {/* Hero 영역 */}
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-sky-100 via-white to-white" />
        <Container className="py-14">
          {/* <span className="inline-block rounded-full bg-slate-100 px-3 py-1 text-[11px]">
            {t("news.badge", "Infinity Training Center News&Event")}
          </span> */}
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-blue-900 md:text-5xl">
            {t("news.title", "News&Event")}
          </h1>
        </Container>
      </section>

      <Container className="py-10">
        {/* 상태 영역 */}
        {loading && (
          <div className="rounded-2xl border bg-white p-8 text-center text-slate-500">
            {t("news.loading", "News&Event을 불러오는 중입니다...")}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="rounded-2xl border bg-white p-8 text-center text-slate-500">
            {t("news.empty", "등록된 News&Event이 없습니다.")}
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            {/* 총 개수 */}
            <div className="mb-4 text-xs text-slate-500">
              {t("news.total", "총")}{" "}
              <span className="font-semibold text-slate-700">
                {totalElements}
              </span>{" "}
              {t("news.countUnit", "건")}
            </div>

            {/* 리스트 */}
            <div className="grid gap-4">
              {items.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex flex-col gap-3 rounded-2xl border bg-white p-4 shadow-sm transition hover:-translate-y-[1px] hover:shadow-md"
                >
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-center gap-2 text-[11px] font-medium text-slate-500">
                      <span>{fmtDate(item.createdAt)}</span>
                    </div>
                    <h2 className="mt-1 line-clamp-2 text-base font-bold text-slate-900 md:text-lg">
                      {item.title}
                    </h2>
                    <p className="mt-2 line-clamp-2 text-xs text-slate-600 md:text-sm">
                      {getPreviewText(item)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            {/* 페이지네이션 */}
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={onPrev}
                disabled={page <= 0}
                className="rounded-lg border px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("news.prev", "이전")}
              </button>
              <span className="text-xs text-slate-500">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                onClick={onNext}
                disabled={page >= totalPages - 1}
                className="rounded-lg border px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("news.next", "다음")}
              </button>
            </div>
          </>
        )}
      </Container>
    </main>
  );
}
